import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useGoogleMaps from "@insite/client-framework/Common/Hooks/useGoogleMaps";
import useWarehouseFilterSearch from "@insite/client-framework/Common/Hooks/useWarehouseFilterSearch";
import useWarehouseGoogleMarkers from "@insite/client-framework/Common/Hooks/useWarehouseGoogleMarkers";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { getGeoCodeFromLatLng } from "@insite/client-framework/Common/Utilities/GoogleMaps/getGeoCodeFromAddress";
import withDynamicGoogleMaps, { HasDynamicGoogleMaps } from "@insite/client-framework/Common/withDynamicGoogleMaps";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetDrawerIsOpen";
import loadWarehouses from "@insite/client-framework/Store/Components/FindLocationModal/Handlers/LoadWarehouses";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getQueryStrings } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWarehousesDataView } from "@insite/client-framework/Store/Data/Warehouses/WarehousesSelectors";
import translate from "@insite/client-framework/Translate";
import { PaginationModel, WarehouseDto, WarehouseModel } from "@insite/client-framework/Types/ApiModels";
import AddressInfoCondensedDisplay, {
    AddressInfoCondensedDisplayStyles,
} from "@insite/content-library/Components/AddressInfoCondensedDisplay";
import DistanceDisplay, { DistanceUnitOfMeasure } from "@insite/content-library/Components/DistanceDisplay";
import WarehouseFindLocationPagination from "@insite/content-library/Components/WarehouseFindLocationPagination";
import WarehouseGoogleMap, { WarehouseGoogleMapStyles } from "@insite/content-library/Components/WarehouseGoogleMap";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import baseTheme from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import Icon, { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import MapPin from "@insite/mobius/Icons/MapPin";
import Phone from "@insite/mobius/Icons/Phone";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { PaginationPresentationProps } from "@insite/mobius/Pagination";
import Radio, { RadioComponentProps } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupProps } from "@insite/mobius/RadioGroup";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CustomCross from "@WhiteCap/Icons/CustomCross";
import WarehouseModalLocationCall from "@WhiteCap/Icons/WarehouseModalLocationCall";
import WarehouseModalLocationDirections from "@WhiteCap/Icons/WarehouseModalLocationDirections";
import WarehouseModalLocationHours from "@WhiteCap/Icons/WarehouseModalLocationHours";
import GoogleMapsDirectionLink from "@WhiteCap/Overrides/Components/GoogleMapsDirectionLink";
import WarehouseHoursLink from "@WhiteCap/Overrides/Components/WarehouseHoursLink";
import CustomApplicationState from "@WhiteCap/Store/Data/CustomApplicationState/CustomApplicationState";
import { formattedPhoneNumber } from "@WhiteCap/Utils/GetFormattedPhoneNumber";
import { kmToMile } from "@WhiteCap/Utils/ValueConversion";
import parse from "html-react-parser";
import trim from "lodash/trim";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    modalIsOpen: boolean;
    onWarehouseSelected: (warehouse: WarehouseModel) => void;
    onModalClose: () => void;
    forCart?: boolean;
    extendedStyles?: FindLocationModalStyles;
    inventoryWarehouses?: WarehouseDto[] | undefined;
    isWarehouseInventoryLoading?: boolean;
    warehouseInventoryErrorMessage?: string;
    useLoadScript: ({ googleMapsApiKey, region }: { googleMapsApiKey: any; region?: string }) => { isLoaded: boolean };
}

// const mapStateToProps = (state: CustomApplicationState) => ({
//     warehousesDataView: getWarehousesDataView(state, state.components.findLocationModal.getWarehousesParameter),
//     session: state.context.session,
//     settings: getSettingsCollection(state),
//     fulfillmentMethod: state.components.addressDrawer.fulfillmentMethod,
//     changeCustomerWarehouse: state.WhiteCap.changeCustomerWarehouseData,
// });

const mapStateToProps = (state: CustomApplicationState, { forCart }: OwnProps) => {
    const { cartId } = getQueryStrings(state);
    const cart = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;
    const pickUpWarehouse = forCart ? cart?.defaultWarehouse : getSession(state).pickUpWarehouse;

    return {
        warehousesDataView: getWarehousesDataView(state, state.components.findLocationModal.getWarehousesParameter),
        pickUpWarehouse,
        settings: getSettingsCollection(state),
        fulfillmentMethod: state.components.addressDrawer.fulfillmentMethod,
        changeCustomerWarehouse: state.WhiteCap.changeCustomerWarehouseData,
    };
};

const mapDispatchToProps = {
    loadWarehouses,
    setDrawerIsOpen,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasDynamicGoogleMaps;

export interface FindLocationModalStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    modal?: ModalPresentationProps;
    modalContent?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    form?: InjectableCss;
    searchGridContainer?: GridContainerProps;
    searchTextFieldGridItem?: GridItemProps;
    searchTextField?: TextFieldPresentationProps;
    searchRadiusGridItem?: GridItemProps;
    searchRadiusText?: TextFieldPresentationProps;
    searchButtonGridItem?: GridItemProps;
    searchButton?: ButtonPresentationProps;
    searchResultsTextGridItem?: GridItemProps;
    searchResultsText?: TypographyPresentationProps;
    distanceUnitOfMeasureGridItem?: GridItemProps;
    distanceUnitOfMeasureRadioGroup?: RadioGroupProps;
    distanceUnitOfMeasureImperialRadio?: RadioComponentProps;
    distanceUnitOfMeasureMetricRadio?: RadioComponentProps;
    siteMessageGridItem?: GridItemProps;
    siteMessageGeocodeErrorText?: TypographyPresentationProps;
    siteMessageResultsErrorText?: TypographyPresentationProps;
    searchResultGridItem?: GridItemProps;
    searchResultGridContainer?: GridContainerProps;
    searchResultLeftGridItem?: GridItemProps;
    searchResultWarehouseDetailsGridContainer?: GridContainerProps;
    searchResultDisplayNumberGridItem?: GridItemProps;
    warehouseDisplayNumberText?: TypographyPresentationProps;
    searchResultWarehouseGridItem?: GridItemProps;
    warehouseNameText?: TypographyPresentationProps;
    warehouseNameLink?: LinkPresentationProps;
    searchResultAddressInfoOffsetGridItem?: GridItemProps;
    searchResultAddressInfoIcon?: IconPresentationProps;
    searchResultAddressInfoGridItem?: GridItemProps;
    warehouseAddressInfoCondensed?: AddressInfoCondensedDisplayStyles;
    searchResultLinksAndDistanceContainer?: GridContainerProps;
    searchResultPhoneOffsetGridItem?: GridItemProps;
    searchResultPhoneIcon?: IconPresentationProps;
    searchResultPhoneGridItem?: GridItemProps;
    searchResultPhoneLink?: LinkPresentationProps;
    searchResultSelectGridItem?: GridItemProps;
    searchResultSelectButton?: ButtonPresentationProps;
    searchResultLinksOffsetGridItem?: GridItemProps;
    searchResultHoursGridItem?: GridItemProps;
    warehouseHoursLink?: LinkPresentationProps;
    searchResultGoogleMapsDirectionLinkGridItem?: GridItemProps;
    warehouseGoogleMapsDirectionLink?: LinkPresentationProps;
    searchResultDistanceGridItem?: GridItemProps;
    searchResultDistanceHourGridItem?: GridItemProps;
    warehouseDistanceDisplayText?: TypographyPresentationProps;
    searchResultDividerGridItem?: GridItemProps;
    searchResultDivider?: InjectableCss;
    paginationGridItem?: GridItemProps;
    pagination?: PaginationPresentationProps;

    rightColumnGridItem?: GridItemProps;
    currentlySelectedGridContainer?: GridContainerProps;
    currentlySelectedHeaderGridItem?: GridItemProps;
    currentlySelectedHeaderText?: TypographyPresentationProps;
    currentlySelectedDetailsGridItem?: GridItemProps;
    selectedWarehouseGridContainer?: GridContainerProps;
    selectedWarehouseDetailsGridItem?: GridItemProps;
    selectedWarehouseNameText?: TypographyPresentationProps;
    selectedWarehouseAddressDisplay?: AddressInfoCondensedDisplayStyles;
    selectedWarehousePhoneLink?: LinkPresentationProps;
    selectedWarehouseLinksGridItem?: GridItemProps;
    selectedWarehouseLink?: LinkPresentationProps;
    selectedWarehouseGoogleMapsDirectionLink?: LinkPresentationProps;
    selectedWarehouseDistanceText?: TypographyPresentationProps;
    warehouseGoogleMapGridItem?: GridItemProps;
    warehouseGoogleMap?: WarehouseGoogleMapStyles;
    warehouseHoursModal?: ModalPresentationProps;
    hoursContainer?: InjectableCss;
    availableStockGridItem?: GridItemProps;
    availableStockText?: TypographyPresentationProps;
    outOfStockText?: TypographyPresentationProps;
    pipeSeparatorTypography?: TypographyPresentationProps;
    locationPhoneIcon?: IconPresentationProps;
    locationHoursIcon?: IconPresentationProps;
    locationDirectionsIcon?: IconPresentationProps;
    searchResultMobileSelectButton?: ButtonPresentationProps;
    selectedWarehousePhoneLinkGridItem?: GridItemProps;
    selectButtonMobileGridItem?: GridItemProps;
    mobileViewIconGridContainer?: GridContainerProps;
    distanceAndWarehouseNameSeparator?: TypographyPresentationProps;
    currentlySelectedSearchResultDivider?: InjectableCss;
    selectedWarehouseAddressGridItem?: GridItemProps;
}

export const findLocationModalStyles: FindLocationModalStyles = {
    centeringWrapper: {
        css: css`
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    selectedWarehouseAddressGridItem: {
        css: css`
            padding-top: 0px;
            padding-bottom: 10px;
        `,
    },
    modal: {
        sizeVariant: "large",
        cssOverrides: {
            modalTitle: css`
                padding: 10px 20px;
            `,
            modalContent: css`
                padding: 20px;
            `,
        },
    },
    modalContent: {
        gap: 30,
    },
    leftColumnGridItem: {
        width: [12, 12, 12, 6, 5],
        css: css`
            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 15px 20px !important;
                    `,
                    css``,
                    css`
                        padding: 15px 20px !important;
                    `,
                    css``,
                    css``,
                ])}
        `,
    },
    form: {
        css: css`
            width: 100%;
        `,
    },
    searchGridContainer: {
        gap: 0,
    },
    searchTextFieldGridItem: {
        width: 12,
    },
    searchRadiusGridItem: {
        width: 8,
        css: css`
            padding-top: 10px;
            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding-right: 15px;
                    `,
                    css`
                        padding-right: 15px;
                    `,
                    css`
                        padding-right: 15px;
                    `,
                    css``,
                    css``,
                ])}
        `,
    },
    searchButtonGridItem: {
        width: 4,
        css: css`
            padding-top: 10px;
            display: inline-block;
            text-align: right;
            align-self: flex-end;
        `,
    },
    searchResultsTextGridItem: {
        width: 4,
        align: "middle",
        css: css`
            padding-top: 5px;
            padding-bottom: 10px;
        `,
    },
    searchResultsText: {
        css: css`
            margin-top: 10px;
        `,
    },
    distanceUnitOfMeasureGridItem: {
        width: 8,
        align: "middle",
        css: css`
            padding-top: 5px;
            padding-bottom: 10px;
        `,
    },
    distanceUnitOfMeasureRadioGroup: {
        css: css`
            display: inline-block;
            width: 100%;
            flex-direction: row;
            text-align: right;
            & > div {
                display: inline-flex;
            }
        `,
    },
    siteMessageGridItem: {
        width: 12,
    },
    siteMessageGeocodeErrorText: {
        color: "danger",
        weight: "bold",
    },
    siteMessageResultsErrorText: {
        color: "danger",
        weight: "bold",
    },
    searchResultGridItem: {
        width: 12,
        css: css`
            padding: 10px 0px;
        `,
    },
    searchResultGridContainer: {
        gap: 10,
    },
    searchResultLeftGridItem: {
        width: [12, 9, 9, 9, 9],
    },
    searchResultWarehouseDetailsGridContainer: {
        gap: 10,
    },
    searchResultDisplayNumberGridItem: {
        width: 2,
        css: css`
            justify-content: center;
            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    css``,
                    css``,
                ])}
        `,
    },
    warehouseDisplayNumberText: {
        color: "primary",
        weight: "bold",
    },
    searchResultWarehouseGridItem: {
        width: 10,
        css: css`
            padding-top: 0px;
        `,
    },
    warehouseNameText: {
        weight: "bold",
        ellipsis: true,
        css: css`
            color: rgb(40, 81, 155);
        `,
    },
    warehouseNameLink: {
        css: css`
            > span {
                font-weight: bold;
                text-overflow: ellipsis;
                white-space: nowrap;
                /* color: ${getColor("text.link")}; */
                color: rgb(40, 81, 155);
            }
        `,
    },
    searchResultAddressInfoOffsetGridItem: {
        width: 2,
        css: css`
            justify-content: center;
        `,
    },
    searchResultAddressInfoIcon: {
        color: "primary",
    },
    searchResultAddressInfoGridItem: {
        width: 10,
    },
    searchResultPhoneOffsetGridItem: {
        width: 2,
        css: css`
            justify-content: center;
        `,
    },
    searchResultPhoneIcon: {
        color: "primary",
    },
    searchResultPhoneGridItem: {
        width: 12,
    },
    searchResultSelectGridItem: {
        width: 3,
        css: css`
            justify-content: end;
            align-items: center;
            margin-bottom: 51px;
        `,
    },
    searchResultSelectButton: {
        variant: "tertiary",
        css: css`
            width: 72px;
            min-width: max-content;
            padding: 4px 8px;
            margin-bottom: 20px;
        `,
    },
    searchResultMobileSelectButton: {
        variant: "tertiary",
        css: css`
            margin-left: auto;
            margin-right: 0px;
            width: 72px;
            min-width: max-content;
            padding: 4px 8px;
        `,
    },
    searchResultLinksAndDistanceContainer: {
        gap: 0,
        css: css`
            width: 100%;
            padding: 0;
            margin: 0;
        `,
    },
    searchResultLinksOffsetGridItem: {
        width: 2,
    },
    searchResultHoursGridItem: {
        css: css`
            margin-left: 5px !important;
            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding-left: 0px;
                    `,
                    css``,
                    css`
                        padding-left: 0px;
                    `,
                    css``,
                    css`
                        padding-left: 0px;
                    `,
                ])}
        `,
        width: 12,
    },
    searchResultGoogleMapsDirectionLinkGridItem: {
        width: 4,
        css: css`
            margin-right: 28px !important;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        margin-right: 26px !important;
                    `,
                    css`
                        margin-right: 26px !important;
                    `,
                    css`
                        margin-right: 47px !important;
                    `,
                    null,
                    null,
                ]);
            }}
        `,
    },
    searchResultDistanceGridItem: {
        css: css`
            padding-left: 77px;
            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding-left: 40px !important;
                    `,
                    css``,
                    css`
                        padding-left: 170px !important;
                    `,
                    null,
                    null,
                ])}
        `,
        width: 4,
    },
    searchResultDistanceHourGridItem: {
        css: css`
            padding-left: 6px;
            /* ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding-left: 40px !important;
                    `,
                    css``,
                    css`
                        padding-left: 170px !important;
                    `,
                    null,
                    null,
                ])} */
        `,
        width: 12,
    },
    searchResultDividerGridItem: {
        width: 12,
    },
    searchResultDivider: {
        css: css`
            margin: 15px 0px;
            border: 1px solid rgba(74, 74, 74, 0.2);
            width: 100%;
        `,
    },
    currentlySelectedSearchResultDivider: {
        css: css`
            margin: 0;
            border: 1px solid rgba(74, 74, 74, 0.2);
            width: 100%;
        `,
    },
    paginationGridItem: {
        width: 12,
        css: css`
            justify-content: center;
            ${({ theme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-top: 25px !important;
                        `,
                        css`
                            margin-top: 25px !important;
                        `,
                        css`
                            margin-top: 25px !important;
                        `,
                        null,
                        null,
                    ],
                    "min",
                )}
        `,
    },
    pagination: {
        cssOverrides: {
            perPageSelect: css`
                ${({ theme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                display: none;
                            `,
                            null,
                            null,
                            null,
                            null,
                        ],
                        "min",
                    )}
            `,
        },
    },
    rightColumnGridItem: {
        width: [12, 12, 12, 6, 7],
        css: css`
            padding: 0px !important;
        `,
    },
    currentlySelectedGridContainer: {
        css: css`
            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 15px 20px !important;
                    `,
                    css`
                        padding: 15px 20px !important;
                    `,
                    css`
                        padding: 15px 20px !important;
                    `,
                    css`
                        padding: 15px 20px !important;
                    `,
                    css`
                        padding: 15px 20px !important;
                    `,
                ])}
        `,
    },

    currentlySelectedHeaderGridItem: {
        width: 12,
        css: css`
            margin-left: 15px !important;
            margin-right: 15px !important;
            margin-bottom: 0 !important;
            padding-bottom: 0 !important;
            background-color: #f7f8f9;
        `,
    },
    currentlySelectedHeaderText: {
        weight: "bold",
    },
    currentlySelectedDetailsGridItem: {
        width: 12,
        css: css`
            margin-left: 15px !important;
            margin-right: 15px !important;
            padding-top: 10px !important;
            background-color: #f7f8f9;
        `,
    },
    selectedWarehouseDetailsGridItem: {
        width: 12,
        css: css`
            padding: 0px 5px;
        `,
    },
    selectedWarehouseNameText: {
        weight: "bold",
        css: css`
            color: rgb(40, 81, 155);
        `,
    },
    selectedWarehouseLinksGridItem: {
        width: 6,
        css: css`
            padding-top: 0px;
            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    css``,
                    css``,
                ])}
        `,
    },
    warehouseGoogleMapGridItem: {
        width: 12,
    },
    warehouseHoursModal: {
        sizeVariant: "small",
        cssOverrides: {
            modalTitle: css`
                padding: 10px 20px;
            `,
            modalContent: css`
                padding: 20px;
            `,
        },
    },
    hoursContainer: {
        css: css`
            white-space: pre-wrap;
        `,
    },
    availableStockGridItem: {
        width: 12,
        css: css`
            padding-bottom: 25px;
        `,
    },
    availableStockText: {
        css: css`
            padding-left: 15px;
            padding-right: 15px;
            background-color: #3cb24d;
            color: white;
            font-size: 14px;
            border-radius: 5px;
            margin-right: 15px;
        `,
    },
    outOfStockText: {
        css: css`
            padding-left: 15px;
            padding-right: 15px;
            background-color: #ca4200;
            color: white;
            font-size: 14px;
            border-radius: 5px;
            margin-right: 15px;
        `,
    },
    pipeSeparatorTypography: {
        css: css`
            margin-left: 10px;
            margin-right: 10px;
        `,
    },
    selectedWarehouseGridContainer: {
        gap: 10,
        css: css`
            margin-top: 0px;
        `,
    },
    locationPhoneIcon: {
        size: 30,
        css: css`
            margin-top: 15px;
            margin-right: 24px;
        `,
    },
    locationDirectionsIcon: {
        size: 30,
        css: css`
            margin-top: 15px;
            margin-right: 24px;
        `,
    },
    locationHoursIcon: {
        size: 30,
        css: css`
            margin-top: 15px;
            margin-right: 24px;
        `,
    },
    searchButton: {
        variant: "primary",
        css: css`
            color: black;
        `,
    },
    selectedWarehousePhoneLinkGridItem: {
        width: 12,
        css: css`
            margin-left: 5px;
            padding-bottom: 0px;
            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    css``,
                    css``,
                ])}
        `,
    },
    selectButtonMobileGridItem: {
        width: 6,
        css: css`
            padding-top: 10px;
            display: inline-block;
            text-align: right;
            align-self: flex-end;
        `,
    },
    mobileViewIconGridContainer: {
        isFullWidth: true,
        css: css`
            margin-left: 5px;
            margin-right: 0px;
            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    null,
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                ])}
        `,
    },
    selectedWarehouseDistanceText: {
        weight: "bold",
    },
    warehouseDistanceDisplayText: {
        weight: "bold",
        css: css`
            min-width: max-content;
        `,
    },
    distanceAndWarehouseNameSeparator: {
        weight: "bold",
        css: css`
            color: rgb(40, 81, 155);
            margin-left: 10px;
            margin-right: 10px;
        `,
    },
};

const StyledForm = getStyledWrapper("form");
const StyledHr = getStyledWrapper("hr");

let lastFocusableElement: any = null;

const FindLocationModal = ({
    pickUpWarehouse,
    settings,
    warehousesDataView,
    modalIsOpen,
    onWarehouseSelected,
    onModalClose,
    extendedStyles,
    loadWarehouses,
    useLoadScript,
    fulfillmentMethod,
    changeCustomerWarehouse,
    inventoryWarehouses,
    isWarehouseInventoryLoading,
    warehouseInventoryErrorMessage,
    setDrawerIsOpen,
}: Props) => {
    const [styles] = useState(() => mergeToNew(findLocationModalStyles, extendedStyles));
    // EC-507 Search warehouse by radius setting change
    // Set default to KM option instead of miles
    const [distanceUnitOfMeasure, setDistanceUnitOfMeasure] = useState<DistanceUnitOfMeasure>("Metric");
    const [warehouseHoursToDisplay, setWarehouseHoursToDisplay] = useState<WarehouseModel | undefined>(undefined);
    const [warehouseHoursToDisplayModalOpen, setWarehouseHoursToDisplayModalOpen] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const { googleMapsApiKey } = settings.websiteSettings;
    const { isLoaded } = useLoadScript({ googleMapsApiKey, region: "ca" });
    const {
        googleMap,
        setGoogleMap,
        currentLocation,
        setCurrentLocation,
        isGoogleMapsScriptsLoaded,
        setBounds,
        setMapCenter,
        locationKnown,
        setLocationKnown,
    } = useGoogleMaps({
        isLoaded,
        isShown: modalIsOpen,
    });

    // Manage Selected Warehouse
    const [selectedWarehouse, setSelectedWarehouse] = useState<WarehouseModel | undefined>(undefined);
    const [showSelectedWarehouse, setShowSelectedWarehouse] = useState<boolean>(false);
    const [defaultRadius, setDefaultRadius] = useState<number>(0);
    useEffect(() => {
        setSelectedWarehouse(
            changeCustomerWarehouse?.Warehouse
                ? changeCustomerWarehouse.Warehouse
                : pickUpWarehouse || undefined,
        );
    }, [pickUpWarehouse, changeCustomerWarehouse?.Warehouse]);

    // Manage Local Warehouses
    const [warehouses, setWarehouses] = useState<WarehouseModel[]>([]);
    const [resultCount, setResultCount] = useState<number>(0);
    const [warehousesPagination, setWarehousesPagination] = useState<PaginationModel | undefined>();
    const [sortedWarehouses, setSortedWarehouses] = useState<WarehouseModel[]>(warehousesDataView.value || []);
    const [isInStock, setIsInStock] = useState("inStock");
    const [radius, setRadius] = React.useState(0);

    // Manage Loading Warehouses on State Changes
    const {
        doSearch,
        warehouseSearchFilter,
        setWarehouseSearchFilter,
        radiusSearchFilter,
        setRadiusSearchFilter,
        setPage,
        setPageSize,
    } = useWarehouseFilterSearch({
        loadWarehouses,
        currentLocation,
        setCurrentLocation,
        setLocationKnown,
        defaultRadius,
        selectedWarehouse,
        setSelectedWarehouse,
        setShowSelectedWarehouse,
    });

    // Manage Bounds of Map
    useEffect(() => {
        const bounds = [];
        closeInfoWindows();
        warehouses.forEach(warehouse => {
            bounds.push({ lat: warehouse.latitude, lng: warehouse.longitude });
        });
        if (showSelectedWarehouse && selectedWarehouse) {
            bounds.push({ lat: selectedWarehouse.latitude, lng: selectedWarehouse.longitude });
        }
        setBounds(bounds);
    }, [googleMap, warehouses, selectedWarehouse, showSelectedWarehouse, currentLocation]);

    useEffect(() => {
        setRadiusSearchFilter(50);
    }, []);

    // Manage Map Markers
    const { mapMarkersElements, currentLocationInfoWindow, warehouseInfoWindow, closeInfoWindows, getWarehouseNumber } =
        useWarehouseGoogleMarkers({
            warehouses,
            warehouseSearchFilter,
            warehousesPagination,
            currentLocation,
            selectedWarehouse,
            showSelectedWarehouse,
        });

    useEffect(() => {
        if (!isGoogleMapsScriptsLoaded) {
            return;
        }
        if (warehousesDataView.value && !warehousesDataView.isLoading) {
            const {
                pagination,
                value: warehouses,
                defaultRadius,
                defaultLatitude,
                defaultLongitude,
            } = warehousesDataView;
            // Find default location
            getGeoCodeFromLatLng(defaultLatitude, defaultLongitude)
                .then(result => {
                    const isSearchTextAlreadyFormatted = !!result.find(
                        o => o.formatted_address === warehouseSearchFilter,
                    );
                    if (isSearchTextAlreadyFormatted) {
                        return;
                    }
                    setWarehouseSearchFilter(result[0].formatted_address);
                })
                .finally(() => {
                    // EC-500 - filter to hide distribution center for warehouses using custom properties (isDistributionCenter)
                    const warehouseFiltered = warehouses.filter(
                        item =>
                            !item.properties?.isDistributionCenter ||
                            !(item.properties?.isDistributionCenter.toLowerCase() === "true"),
                    );
                    const resultCount = warehouseFiltered.length || 0;

                    setWarehousesPagination(pagination || undefined);
                    setResultCount(
                        resultCount ? resultCount : pagination?.totalItemCount ? pagination?.totalItemCount : 0,
                    );
                    setDefaultRadius(defaultRadius);
                    setRadiusSearchFilter(defaultRadius);
                    if (!radius) {
                        setRadius(defaultRadius);
                    }
                    setMapCenter(new google.maps.LatLng(defaultLatitude, defaultLongitude));
                    setWarehouses(warehouseFiltered || []);

                    setIsLoading(false);
                });
        } else {
            setIsLoading(true);
        }
    }, [warehousesDataView, isGoogleMapsScriptsLoaded]);

    useEffect(() => {
        // EC-510 Filters for inventory availablity
        // Sort the warehouses array so that items with a qty field that is either null or 0 are moved to the top of the array
        // EC-507 Set default warehouse for sortedWarehouse

        if (inventoryWarehouses && warehousesDataView.value) {
            let inventoryDict: { [key: string]: number } = {};
            inventoryWarehouses.forEach(item => {
                inventoryDict[item.name] = item.qty;
            });

            let mergedWarehouses = warehousesDataView.value.map(warehouse => {
                let warehouseName = warehouse.name;
                return {
                    ...warehouse,
                    qty: inventoryDict[warehouseName] !== undefined ? inventoryDict[warehouseName] : null,
                };
            });
            if (isInStock === "inStock") {
                const newmergedWarehouses = mergedWarehouses.sort((a, b) => {
                    if (a.qty !== null && a.qty !== 0 && (b.qty === null || b.qty === 0)) {
                        return -1;
                    } else if ((a.qty === null || a.qty === 0) && b.qty !== null && b.qty !== 0) {
                        return 1;
                    } else {
                        return 0;
                    }
                });

                setSortedWarehouses(newmergedWarehouses);
            } else {
                const newmergedWarehouses = mergedWarehouses.sort((a, b) => {
                    if ((a.qty === null || a.qty === 0) && b.qty !== null && b.qty !== 0) {
                        return -1;
                    } else if (a.qty !== null && a.qty !== 0 && (b.qty === null || b.qty === 0)) {
                        return 1;
                    } else {
                        return 0;
                    }
                });

                setSortedWarehouses(newmergedWarehouses);
            }
        } else {
            setSortedWarehouses(warehouses || []);
        }
    }, [warehousesDataView.value, inventoryWarehouses, isInStock, warehouses]);

    // Manage modal focus when tabbing through the map insert
    const keysPressed = {
        shift: false,
        tab: false,
    };

    let focusableElementInterval: any = null;

    const updateLastFocusableElement = () => {
        const focusableElements = document.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (focusableElements.length > 0) {
            lastFocusableElement = focusableElements[focusableElements.length - 1];
        }
    };

    const handleKeydownTabInModal = (event: KeyboardEvent) => {
        // collect what keys are being pressed in the case that the user is trying to shift+tab while on the close modal button
        if (event.key === "Tab") {
            keysPressed.tab = true;
        } else if (event.key === "Shift") {
            keysPressed.shift = true;
        }
        // if straight tab and on the final focusable element (the terms of use html page link) refocus onto the close modal button
        if (event.key === "Tab" && !keysPressed.shift) {
            const currentElement = document.activeElement;
            // using the .html here as it is the only link on the page to an html page. Cannot rely on checking "terms of use" is the inner HTML since that will not always be in English

            if (currentElement === lastFocusableElement) {
                event.preventDefault();
                document.getElementById("CloseModalButton")?.focus();
            }
        } else if ((event.key === "Tab" && keysPressed.shift) || (event.key === "Shift" && keysPressed.tab)) {
            // if pressing shift+tab and the focus is on the close modal button, send the focus to the terms of use element which is the last anchor tag on the google map
            if (document.activeElement?.id === "CloseModalButton" && lastFocusableElement) {
                event.preventDefault();
                lastFocusableElement.focus();
            }
        }
    };

    const handleKeyupTabInModal = (event: KeyboardEvent) => {
        if (event.key === "Tab") {
            keysPressed.tab = false;
        } else if (event.key === "Shift") {
            keysPressed.shift = false;
        }
    };

    useEffect(() => {
        if (modalIsOpen && !warehouseHoursToDisplayModalOpen) {
            window.addEventListener("keydown", handleKeydownTabInModal);
            window.addEventListener("keyup", handleKeyupTabInModal);
            focusableElementInterval = setInterval(updateLastFocusableElement, 1000);
        }
        return () => {
            window.removeEventListener("keydown", handleKeydownTabInModal);
            window.removeEventListener("keyup", handleKeyupTabInModal);
            clearInterval(focusableElementInterval);
        };
    }, [modalIsOpen, warehouseHoursToDisplayModalOpen]);

    const modalCloseHandler = () => {
        onModalClose();
    };

    const handleDistanceUnitOfMeasureChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value as DistanceUnitOfMeasure;
        setDistanceUnitOfMeasure(value);
    };
    // EC-510 Filters for inventory availablity
    const handleInventoryStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setIsInStock(value);
    };

    const handleOpenHoursModal = (warehouse: WarehouseModel) => {
        setWarehouseHoursToDisplayModalOpen(!!warehouse.hours);
        setWarehouseHoursToDisplay(warehouse);
    };

    const handleHoursModalClose = () => {
        setWarehouseHoursToDisplayModalOpen(false);
        setWarehouseHoursToDisplay(undefined);
    };

    const generateOpenWarehouseHoursFor = (warehouse: WarehouseModel) => {
        return () => handleOpenHoursModal(warehouse);
    };

    const generateWarehouseSelectClickHandler = (warehouse: WarehouseModel) => {
        return () => onWarehouseSelected(warehouse);
    };

    const isNotFinalResult = (resultIndex: number) => {
        if (!warehousesPagination) {
            return false;
        }
        return warehousesPagination.pageSize > resultIndex + 1;
    };
    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();
        if (!isLoading) {
            doSearch(warehouseSearchFilter);
        }
    };
    const handleSearchRadiusChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        const fixedRadius = Number(event.target.value) * kmToMile;
        setRadius(Number(event.target.value));
        setRadiusSearchFilter(Math.round(fixedRadius));
    };

    const getQtyAvailableByName = (name: string) => {
        if (inventoryWarehouses) {
            const warehouse = inventoryWarehouses.filter(w => w.name === name)?.[0];
            return warehouse?.qty ?? 0;
        }
        return 0;
    };

    const removePrefix = (input: string): string => {
        const prefix = "White Cap - ";
        if (input.startsWith(prefix)) {
            return input.substring(prefix.length);
        }
        return input;
    };

    const handleWarehouseClick = () => {
        onModalClose();
        setDrawerIsOpen({ isOpen: false });
    };

    const SelectedLocation = (
        <>
            <GridItem {...styles.currentlySelectedHeaderGridItem}>
                <Typography {...styles.currentlySelectedHeaderText}>{translate("Selected Branch")}</Typography>
            </GridItem>
            {selectedWarehouse && (
                <GridItem {...styles.currentlySelectedDetailsGridItem}>
                    <GridContainer {...styles.selectedWarehouseGridContainer}>
                        {inventoryWarehouses && (
                            <GridItem {...styles.availableStockGridItem}>
                                {getQtyAvailableByName(selectedWarehouse.name) === 0 ? (
                                    <Typography {...styles.outOfStockText}>
                                        {getQtyAvailableByName(selectedWarehouse.name)}
                                    </Typography>
                                ) : (
                                    <Typography {...styles.availableStockText}>
                                        {/*EC-509 Comma-separate the inventory count to make it easier for the user to read the item count. */}
                                        {getQtyAvailableByName(selectedWarehouse.name).toLocaleString("en-US")}
                                    </Typography>
                                )}
                                <Typography>Available</Typography>
                            </GridItem>
                        )}
                        <GridItem {...styles.selectedWarehouseDetailsGridItem}>
                            {selectedWarehouse.distance > 0.01 && (
                                <>
                                    <DistanceDisplay
                                        {...styles.selectedWarehouseDistanceText}
                                        distance={selectedWarehouse.distance}
                                        unitOfMeasure={distanceUnitOfMeasure}
                                    />
                                    <Typography {...styles.distanceAndWarehouseNameSeparator}>|</Typography>
                                </>
                            )}
                            <Typography
                                {...styles.selectedWarehouseNameText}
                                data-test-selector="pickupLocationModal_selectedWarehouseName"
                            >
                                {removePrefix(selectedWarehouse.description || selectedWarehouse.name)}
                            </Typography>
                        </GridItem>
                        <GridItem width={12} {...styles.selectedWarehouseAddressGridItem}>
                            <AddressInfoCondensedDisplay
                                {...selectedWarehouse}
                                extendedStyles={styles.selectedWarehouseAddressDisplay}
                            />
                        </GridItem>
                        <GridItem {...styles.selectedWarehousePhoneLinkGridItem}>
                            {selectedWarehouse.phone && (
                                <Link
                                    href={`tel: ${selectedWarehouse.phone}`}
                                    {...styles.selectedWarehousePhoneLink}
                                    target="_blank"
                                >
                                    {/* EC-94 : B2B Website Header = Updating warehouse number to stay in the same format across the website */}
                                    {formattedPhoneNumber(selectedWarehouse.phone)}
                                </Link>
                            )}
                        </GridItem>
                        <GridItem {...styles.selectedWarehouseLinksGridItem}>
                            {selectedWarehouse.hours && (
                                <>
                                    <WarehouseHoursLink
                                        {...styles.selectedWarehouseLink}
                                        warehouse={selectedWarehouse}
                                        onOpenWarehouseHours={handleOpenHoursModal}
                                    />
                                    <Typography {...styles.pipeSeparatorTypography}>|</Typography>
                                </>
                            )}

                            <GoogleMapsDirectionLink
                                {...styles.selectedWarehouseGoogleMapsDirectionLink}
                                {...selectedWarehouse}
                            />
                        </GridItem>
                        <Hidden above="md">
                            <GridContainer style={{ marginLeft: "5px", alignItems: "center" }}>
                                <GridItem width={3}>
                                    <Link
                                        href={`tel:${selectedWarehouse.phone} `}
                                        {...styles.searchResultPhoneLink}
                                        target="_blank"
                                    >
                                        <Icon {...styles.locationPhoneIcon} src={WarehouseModalLocationCall}></Icon>
                                    </Link>
                                </GridItem>
                                <GridItem width={3}>
                                    <WarehouseHoursLink
                                        {...styles.warehouseHoursLink}
                                        iconMode={true}
                                        iconStyleProps={styles.locationHoursIcon}
                                        warehouse={selectedWarehouse}
                                        onOpenWarehouseHours={generateOpenWarehouseHoursFor(selectedWarehouse)}
                                    />
                                </GridItem>
                                <GridItem width={3}>
                                    <GoogleMapsDirectionLink
                                        {...styles.warehouseGoogleMapsDirectionLink}
                                        iconMode={true}
                                        iconStyles={styles.locationDirectionsIcon}
                                        {...selectedWarehouse}
                                    />
                                </GridItem>
                            </GridContainer>
                        </Hidden>
                    </GridContainer>
                </GridItem>
            )}
        </>
    );

    return (
        <Modal
            {...styles.modal}
            headline={
                fulfillmentMethod === FulfillmentMethod.PickUp ? translate("Pick Up Address") : translate("Branches")
            }
            isOpen={modalIsOpen}
            handleClose={modalCloseHandler}
        >
            {isWarehouseInventoryLoading && (
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner {...styles.spinner} />
                </StyledWrapper>
            )}
            {!isWarehouseInventoryLoading && (
                <GridContainer {...styles.modalContent} data-test-selector="findLocationModal">
                    <Hidden above="md">
                        <GridItem {...styles.rightColumnGridItem}>
                            <GridContainer {...styles.currentlySelectedGridContainer}>
                                {SelectedLocation}
                                <GridItem {...styles.searchResultDividerGridItem}>
                                    <StyledHr {...styles.currentlySelectedSearchResultDivider} />
                                </GridItem>
                            </GridContainer>
                        </GridItem>
                    </Hidden>
                    <GridItem {...styles.leftColumnGridItem}>
                        <StyledForm {...styles.form} onSubmit={handleSubmit}>
                            <GridContainer {...styles.searchGridContainer}>
                                <GridItem {...styles.searchTextFieldGridItem}>
                                    <SearchLocationsTextField
                                        styles={styles.searchTextField}
                                        warehouseSearchFilter={warehouseSearchFilter}
                                        setWarehouseSearchFilter={setWarehouseSearchFilter}
                                    />
                                </GridItem>
                                <GridItem {...styles.searchRadiusGridItem}>
                                    <TextField
                                        {...styles.searchRadiusText}
                                        label={translate("Search Radius")}
                                        value={radius}
                                        onChange={handleSearchRadiusChanged}
                                        type="number"
                                        min={0}
                                        placeholder={translate("Search Radius")}
                                    />
                                </GridItem>

                                <GridItem {...styles.searchButtonGridItem}>
                                    <Button
                                        {...styles.searchButton}
                                        type="submit"
                                        data-test-selector="findLocationModal_searchButton"
                                    >
                                        {translate("Search")}
                                    </Button>
                                </GridItem>
                                {/* EC-803 Remove warehouse sorting and filters in view availability pop-Up */}
                                {/* {inventoryWarehouses && (
                                    <GridItem
                                        width={12}
                                        css={css`
                                            padding-top: 20px;
                                        `}
                                    >
                                      
                                        <RadioGroup
                                            value={isInStock}
                                            onChangeHandler={handleInventoryStatusChange}
                                            label={siteMessage("Warehouse_Filter_By")}
                                            horizontal
                                            labelProps={{
                                                css: css`
                                                    margin-bottom: 1px !important;
                                                `,
                                            }}
                                        >
                                            <Radio {...styles.distanceUnitOfMeasureImperialRadio} value="inStock">
                                                {siteMessage("Warehouse_In_Stock")}
                                            </Radio>
                                            <Radio {...styles.distanceUnitOfMeasureMetricRadio} value="outOfStock">
                                                {siteMessage("Warehouse_Out_of_Stock")}
                                            </Radio>
                                        </RadioGroup>
                                    </GridItem>
                                )} */}

                                <GridItem {...styles.searchResultsTextGridItem}>
                                    <Typography {...styles.searchResultsText}>
                                        {translate("{0} Results").replace("{0}", `${resultCount} `)}
                                    </Typography>
                                </GridItem>
                                {/* EC-803 Update Warehouse Sorting and Filters in View Availability Pop-Up */}
                                <GridItem {...styles.distanceUnitOfMeasureGridItem}>
                                    <RadioGroup
                                        {...styles.distanceUnitOfMeasureRadioGroup}
                                        value={distanceUnitOfMeasure}
                                        onChangeHandler={handleDistanceUnitOfMeasureChange}
                                    >
                                        <Radio {...styles.distanceUnitOfMeasureMetricRadio} value="Metric">
                                            {translate("Kilometers")}
                                        </Radio>
                                        <Radio {...styles.distanceUnitOfMeasureImperialRadio} value="Imperial">
                                            {translate("Miles")}
                                        </Radio>
                                    </RadioGroup>
                                </GridItem>
                                <GridItem {...styles.siteMessageGridItem}>
                                    {!isWarehouseInventoryLoading && warehouseInventoryErrorMessage && (
                                        <Typography {...styles.siteMessageGeocodeErrorText}>
                                            {warehouseInventoryErrorMessage}
                                        </Typography>
                                    )}
                                    {!isLoading && !locationKnown && (
                                        <Typography {...styles.siteMessageGeocodeErrorText}>
                                            {siteMessage("PickUpLocation_GeocodeErrorMessage")}
                                        </Typography>
                                    )}
                                    {!isLoading && locationKnown && warehouses.length === 0 && (
                                        <Typography
                                            data-test-selector="findLocationModal_noresult"
                                            {...styles.siteMessageResultsErrorText}
                                        >
                                            {showSelectedWarehouse
                                                ? siteMessage("PickUpLocation_SelectedLocationIsOnlyResult")
                                                : siteMessage("PickUpLocation_NoResultsMessage")}
                                        </Typography>
                                    )}
                                </GridItem>
                                {(!warehousesDataView || !warehousesDataView.value) && (
                                    <GridItem {...styles.searchResultGridItem}>
                                        <StyledWrapper {...styles.centeringWrapper}>
                                            <LoadingSpinner {...styles.spinner} />
                                        </StyledWrapper>
                                    </GridItem>
                                )}
                                {warehousesDataView &&
                                    warehousesDataView.value &&
                                    sortedWarehouses.map((warehouse, index) => (
                                        <GridItem key={warehouse.id} {...styles.searchResultGridItem}>
                                            <GridContainer
                                                {...styles.searchResultGridContainer}
                                                data-test-selector="findLocationModal_warehouse"
                                                data-test-key={`${warehouse.id}`}
                                            >
                                                <GridItem {...styles.searchResultLeftGridItem}>
                                                    <GridContainer
                                                        {...styles.searchResultWarehouseDetailsGridContainer}
                                                    >
                                                        {/* <GridItem {...styles.searchResultDisplayNumberGridItem}>
                                                        <Typography {...styles.warehouseDisplayNumberText}>
                                                            {getWarehouseNumber(index)}
                                                        </Typography>
                                                    </GridItem> */}
                                                        {inventoryWarehouses && (
                                                            <GridItem {...styles.availableStockGridItem}>
                                                                {getQtyAvailableByName(warehouse.name) === 0 ? (
                                                                    <Typography {...styles.outOfStockText}>
                                                                        {getQtyAvailableByName(warehouse.name)}
                                                                    </Typography>
                                                                ) : (
                                                                    <Typography {...styles.availableStockText}>
                                                                        {/*EC-509 Comma-separate the inventory count to make it easier for the user to read the item count. */}
                                                                        {getQtyAvailableByName(
                                                                            warehouse.name,
                                                                        ).toLocaleString("en-US")}
                                                                    </Typography>
                                                                )}
                                                                <Typography>{translate("Available")}</Typography>
                                                            </GridItem>
                                                        )}
                                                        <GridItem
                                                            {...styles.searchResultWarehouseGridItem}
                                                            data-test-selector={`pickUpLocationModal_warehouseName_${index} `}
                                                        >
                                                            <DistanceDisplay
                                                                {...styles.warehouseDistanceDisplayText}
                                                                distance={warehouse.distance}
                                                                unitOfMeasure={distanceUnitOfMeasure}
                                                                data-test-selector={`pickUpLocationModal_warehouseDistance_${index} `}
                                                            />
                                                            <Typography {...styles.distanceAndWarehouseNameSeparator}>
                                                                |
                                                            </Typography>
                                                            {/* EC-1022 : Warehouse name should be clickable and on click it should redirect to location details page */}
                                                            {/* <Typography {...styles.warehouseNameText}>
                                                                {removePrefix(warehouse.description || warehouse.name)}
                                                            </Typography> */}
                                                            <Link
                                                                {...styles.warehouseNameLink}
                                                                href={`/locationfinder/dealerdetails/${warehouse.name}`}
                                                                onClick={handleWarehouseClick}
                                                            >
                                                                {removePrefix(warehouse.description || warehouse.name)}
                                                            </Link>
                                                        </GridItem>
                                                        <GridItem {...styles.searchResultAddressInfoGridItem}>
                                                            <AddressInfoCondensedDisplay
                                                                extendedStyles={styles.warehouseAddressInfoCondensed}
                                                                {...warehouse}
                                                            />
                                                        </GridItem>
                                                        <Hidden below="lg">
                                                            {warehouse.phone && (
                                                                <>
                                                                    <GridItem {...styles.searchResultPhoneGridItem}>
                                                                        <Link
                                                                            href={`tel:${warehouse.phone} `}
                                                                            {...styles.searchResultPhoneLink}
                                                                            target="_blank"
                                                                        >
                                                                            {/* EC-94 : B2B Website Header = Updating warehouse number to stay in the same format across the website */}
                                                                            {formattedPhoneNumber(warehouse.phone)}
                                                                        </Link>
                                                                    </GridItem>
                                                                </>
                                                            )}
                                                        </Hidden>
                                                    </GridContainer>
                                                </GridItem>
                                                <GridItem {...styles.searchResultSelectGridItem}>
                                                    <Hidden below="lg">
                                                        <Button
                                                            {...styles.searchResultSelectButton}
                                                            type="button"
                                                            onClick={generateWarehouseSelectClickHandler(warehouse)}
                                                            data-test-selector="findLocationModal_warehouseSelect"
                                                        >
                                                            {translate("Select")}
                                                        </Button>
                                                    </Hidden>
                                                </GridItem>
                                                <GridContainer {...styles.searchResultLinksAndDistanceContainer}>
                                                    {/* <GridItem {...styles.searchResultLinksOffsetGridItem}> */}
                                                    {/* This is here to give the grid an indentation */}
                                                    {/* </GridItem> */}
                                                    <GridItem width={12}>
                                                        <Hidden below="lg">
                                                            {warehouse.hours ? (
                                                                <GridItem {...styles.searchResultHoursGridItem}>
                                                                    <WarehouseHoursLink
                                                                        {...styles.warehouseHoursLink}
                                                                        warehouse={warehouse}
                                                                        onOpenWarehouseHours={generateOpenWarehouseHoursFor(
                                                                            warehouse,
                                                                        )}
                                                                        data-test-selector={`pickUpLocationModal_warehouseHours_${index} `}
                                                                    />
                                                                    <Typography {...styles.pipeSeparatorTypography}>
                                                                        |
                                                                    </Typography>
                                                                    <GoogleMapsDirectionLink
                                                                        {...styles.warehouseGoogleMapsDirectionLink}
                                                                        {...warehouse}
                                                                    />
                                                                </GridItem>
                                                            ) : (
                                                                <GridItem {...styles.searchResultDistanceHourGridItem}>
                                                                    <GoogleMapsDirectionLink
                                                                        {...styles.warehouseGoogleMapsDirectionLink}
                                                                        {...warehouse}
                                                                    />
                                                                </GridItem>
                                                            )}
                                                        </Hidden>

                                                        <GridContainer {...styles.mobileViewIconGridContainer}>
                                                            <GridItem width={2}>
                                                                <Link
                                                                    href={`tel:${warehouse.phone} `}
                                                                    {...styles.searchResultPhoneLink}
                                                                    target="_blank"
                                                                >
                                                                    <Icon
                                                                        {...styles.locationPhoneIcon}
                                                                        src={WarehouseModalLocationCall}
                                                                    ></Icon>
                                                                </Link>
                                                            </GridItem>
                                                            <GridItem width={2}>
                                                                <WarehouseHoursLink
                                                                    {...styles.warehouseHoursLink}
                                                                    iconMode={true}
                                                                    iconStyleProps={styles.locationHoursIcon}
                                                                    warehouse={warehouse}
                                                                    onOpenWarehouseHours={generateOpenWarehouseHoursFor(
                                                                        warehouse,
                                                                    )}
                                                                    data-test-selector={`pickUpLocationModal_warehouseHours_${index} `}
                                                                />
                                                            </GridItem>
                                                            <GridItem width={2}>
                                                                <GoogleMapsDirectionLink
                                                                    {...styles.warehouseGoogleMapsDirectionLink}
                                                                    iconMode={true}
                                                                    iconStyles={styles.locationDirectionsIcon}
                                                                    {...warehouse}
                                                                />
                                                            </GridItem>
                                                            <GridItem {...styles.selectButtonMobileGridItem}>
                                                                <Button
                                                                    {...styles.searchResultMobileSelectButton}
                                                                    type="button"
                                                                    onClick={generateWarehouseSelectClickHandler(
                                                                        warehouse,
                                                                    )}
                                                                    data-test-selector="findLocationModal_warehouseSelect"
                                                                >
                                                                    {translate("Select")}
                                                                </Button>
                                                            </GridItem>
                                                        </GridContainer>
                                                    </GridItem>
                                                    {isNotFinalResult(index) && (
                                                        <GridItem {...styles.searchResultDividerGridItem}>
                                                            <StyledHr {...styles.searchResultDivider} />
                                                        </GridItem>
                                                    )}
                                                </GridContainer>
                                            </GridContainer>
                                        </GridItem>
                                    ))}
                                <GridItem {...styles.paginationGridItem}>
                                    <WarehouseFindLocationPagination
                                        {...styles.pagination}
                                        warehouses={warehouses}
                                        warehousesPagination={warehousesPagination}
                                        setPage={setPage}
                                        setPageSize={setPageSize}
                                    />
                                </GridItem>
                            </GridContainer>
                        </StyledForm>
                    </GridItem>

                    <GridItem {...styles.rightColumnGridItem}>
                        <GridContainer {...styles.currentlySelectedGridContainer}>
                            <Hidden below="lg">{SelectedLocation}</Hidden>
                            <GridItem {...styles.warehouseGoogleMapGridItem}>
                                <WarehouseGoogleMap
                                    extendedStyles={styles.warehouseGoogleMap}
                                    currentLocation={currentLocation}
                                    warehouseSearchFilter={warehouseSearchFilter}
                                    distanceUnitOfMeasure={distanceUnitOfMeasure}
                                    setGoogleMap={setGoogleMap}
                                    mapMarkerElements={mapMarkersElements}
                                    warehouseInfoWindow={warehouseInfoWindow}
                                    currentLocationInfoWindow={currentLocationInfoWindow}
                                    handleOpenWarehouseHours={handleOpenHoursModal}
                                    updateLastFocusableElement={updateLastFocusableElement}
                                />
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                </GridContainer>
            )}
            <Modal
                {...styles.warehouseHoursModal}
                headline={translate("Hours")}
                isOpen={warehouseHoursToDisplayModalOpen}
                handleClose={handleHoursModalClose}
            >
                {warehouseHoursToDisplay?.hours && (
                    <StyledWrapper {...styles.hoursContainer}>
                        <table cellPadding={0} style={{ width: 260 }}>
                            <tbody>
                                {JSON.parse(warehouseHoursToDisplay?.hours).map((time: any) => (
                                    // eslint-disable-next-line react/jsx-key
                                    <tr>
                                        <td>
                                            <span>
                                                <strong>{trim(time?.WeekDay)}</strong>
                                            </span>
                                        </td>
                                        <td>
                                            <span>{trim(time?.Hours)}</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </StyledWrapper>
                )}
            </Modal>
        </Modal>
    );
};

interface SearchLocationsTextFieldProps {
    styles?: TextFieldPresentationProps;
    warehouseSearchFilter: string;
    setWarehouseSearchFilter: (filter: string) => void;
}

const SearchLocationsTextField = ({
    styles,
    warehouseSearchFilter,
    setWarehouseSearchFilter,
}: SearchLocationsTextFieldProps) => {
    const handleSearchLocationsChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        setWarehouseSearchFilter(event.target.value);
    };

    const handleClearWarehouseSearchFilter = () => {
        setWarehouseSearchFilter("");
    };

    return (
        <TextField
            {...styles}
            label={translate("Search Branches")}
            value={warehouseSearchFilter}
            onChange={handleSearchLocationsChanged}
            placeholder={translate("Search by location name, city, state, or zip")}
            data-test-selector="findLocationModal_locationSearch"
            iconProps={warehouseSearchFilter ? { src: CustomCross } : undefined}
            iconClickableProps={{
                onClick: () => {
                    handleClearWarehouseSearchFilter();
                },
                type: "button",
            }}
            style={{ paddingRight: "40px" }}
        />
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withDynamicGoogleMaps(FindLocationModal));
